import React from 'react';
import CustomCardComponent from '../CustomCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft as fasChevronLeft, faChevronRight as fasChevronRight } from '@fortawesome/free-solid-svg-icons';

// Types
export interface MenuSectionIncludedItem {
  img: string;
  item: string;
}

export interface MenuSectionItem {
  img: string;
  title: string;
  description?: string;
  subtitle?: string;
  includes?: MenuSectionIncludedItem[];
  price?: string;
  alt?: string;
  id?: string;
  visible?: boolean;
  link?: string;
}

export interface MenuSectionData {
  title?: string;
  items: MenuSectionItem[];
}

export interface MenuSectionProps {
  section?: MenuSectionData; // optional to allow loading states
  pageSize?: number; // number of cards per page in list mode
  initialViewMode?: 'list' | 'grid';
  className?: string;
}

const groupInto = <T,>(arr: T[], size: number) => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const MenuSection: React.FC<MenuSectionProps> = ({
  section,
  pageSize = 3,
  initialViewMode = 'list',
  className = ''
}) => {
  const items = (section?.items || []).filter(item => item.visible !== false);

  const [viewMode, setViewMode] = React.useState<'list' | 'grid'>(initialViewMode);
  const pages = React.useMemo(() => groupInto(items, pageSize), [items, pageSize]);
  const [pageIndex, setPageIndex] = React.useState(0);
  React.useEffect(() => { setPageIndex(index => Math.min(index, Math.max(0, pages.length - 1))); }, [pages.length]);
  const [direction, setDirection] = React.useState<1 | -1>(1); // reserved for future animation direction variants
  const touchStartX = React.useRef<number | null>(null);
  const touchDeltaX = React.useRef<number>(0);

  const goNext = () => {
    if (!pages.length) return;
    setDirection(1);
    setPageIndex(p => (p + 1) % pages.length);
  };
  const goPrev = () => {
    if (!pages.length) return;
    setDirection(-1);
    setPageIndex(p => (p - 1 + pages.length) % pages.length);
  };

  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [pages.length]);

  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      touchStartX.current = e.touches[0].clientX;
      touchDeltaX.current = 0;
    }
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current != null && e.touches.length === 1) {
      touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
    }
  };
  const onTouchEnd = () => {
    const threshold = 60; // px to trigger
    if (Math.abs(touchDeltaX.current) > threshold) {
      if (touchDeltaX.current < 0) {
        goNext();
      } else {
        goPrev();
      }
    }
    touchStartX.current = null;
    touchDeltaX.current = 0;
  };

  return (
    <section className={`max-w-6xl mx-auto pt-20 sm:pt-8 ${className}`}>      
      {section?.title && (
        <div className="mb-8 flex items-center justify-between gap-6">
          <h2 className="hammer text-left text-transparent bg-clip-text bg-gradient-to-r from-sow-white via-amber-300 to-sow-darkGold drop-shadow-[0_2px_6px_rgba(255,216,97,0.35)] tracking-wide leading-tight font-extrabold text-2xl sm:text-3xl md:text-4xl lg:text-5xl">
            <span className="inline-block bg-gradient-to-r from-sow-white via-amber-300 to-sow-darkGold bg-clip-text text-transparent bg-[length:300%_100%] animate-[gradient-sweep_2s_ease-in-out_infinite] relative overflow-hidden">
              {section.title}
            </span>
          </h2>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setViewMode('list')}
              aria-label="List view"
              className={`group rounded-md px-3 py-2 text-xs font-bold tracking-wide transition border flex items-center gap-2 ${viewMode === 'list' ? 'border-amber-400 bg-neutral-800 text-amber-300' : 'border-neutral-700 bg-neutral-800/40 text-neutral-300'}`}
            >
              <span className="inline-block w-3 h-3 bg-amber-400 rounded-sm" />
              LIST
            </button>
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              aria-label="Grid view"
              className={`group rounded-md px-3 py-2 text-xs font-bold tracking-wide transition border flex items-center gap-2 ${viewMode === 'grid' ? 'border-amber-400 bg-neutral-800 text-amber-300' : 'border-neutral-700 bg-neutral-800/40 text-neutral-300'}`}
            >
              <span className="grid grid-cols-2 grid-rows-2 gap-[2px] w-3 h-3">
                <span className="block bg-amber-400 rounded-sm" />
                <span className="block bg-amber-400 rounded-sm" />
                <span className="block bg-amber-400 rounded-sm" />
                <span className="block bg-amber-400 rounded-sm" />
              </span>
              GRID
            </button>
          </div>
        </div>
      )}

      {items.length > 0 ? (
        viewMode === 'list' ? (
          <div
            className="relative"
            aria-label={`${section?.title || 'Menu'} carousel`}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {pages.length > 1 && (
              <>
                <button
                  onClick={goPrev}
                  aria-label="Previous"
                  className="hidden md:flex absolute -left-16 top-1/2 -translate-y-1/2 h-16 w-10 items-center justify-center text-neutral-400 hover:text-amber-300 transition"
                >
                  {/* Casting icon due to duplicate type versions in node_modules causing prefix mismatch */}
                  <FontAwesomeIcon icon={fasChevronLeft as any} className="h-10 w-6 scale-y-[1.35] drop-shadow-[0_0_6px_rgba(0,0,0,0.6)]" />
                </button>
                <button
                  onClick={goNext}
                  aria-label="Next"
                  className="hidden md:flex absolute -right-16 top-1/2 -translate-y-1/2 h-16 w-10 items-center justify-center text-neutral-400 hover:text-amber-300 transition"
                >
                  <FontAwesomeIcon icon={fasChevronRight as any} className="h-10 w-6 scale-y-[1.35] drop-shadow-[0_0_6px_rgba(0,0,0,0.6)]" />
                </button>
              </>
            )}
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-600 ease-[cubic-bezier(.7,.05,.2,.95)]"
                style={{ transform: `translateX(calc(${pageIndex} * -100%))` }}
              >
                {pages.map((page, pi) => (
                  <div
                    key={pi}
                    className="shrink-0 w-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-10 px-px relative"
                    aria-hidden={pi !== pageIndex}
                  >
                    {page.map((item: MenuSectionItem, idx: number) => (
                      <div
                        key={item.id || idx}
                        className={`transition-all duration-500 [animation:fadeSlideIn_.7s_ease] ${
                          pi === pageIndex ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                        }`}
                        style={{ animationDelay: `${idx * 80}ms` }}
                      >
                        <CustomCardComponent
                          price={item.price} alt={item.alt}
                          image={item.img}
                          title={item.title}
                          description={item.description || ''}
                          subDescription={item.subtitle}
                          includedItems={(item.includes || []).map(inc => ({
                            image: inc.img,
                            name: inc.item,
                          }))}
                          link={item.link || ''}
                          addToCartText={item.price ? `ADD ${item.price}` : undefined}
                          onAddToCart={() => console.log('Add to cart', item.title)}
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            {pages.length > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                {pages.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setDirection(i > pageIndex ? 1 : -1);
                      setPageIndex(i);
                    }}
                    aria-label={`Go to page ${i + 1}`}
                    className={`h-2.5 w-2.5 rounded-full transition ring-1 ring-white/10 ${
                      i === pageIndex
                        ? 'bg-amber-400 scale-110 shadow-[0_0_0_4px_rgba(255,193,7,0.15)]'
                        : 'bg-neutral-700 hover:bg-neutral-500'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-6">
            {items.map((item, idx) => {
              const CardInner = (
                <div
                  className="relative overflow-hidden rounded-xl bg-neutral-900 border border-neutral-800 p-2 group hover:border-amber-400 transition h-full"
                >
                  <div className="aspect-[4/5] w-full flex items-center justify-center">
                    <img
                      src={item.img}
                      alt={item.alt || item.title}
                      className="h-full w-full object-contain p-2"
                    />
                  </div>
                  <div className="mt-2 text-center text-xs font-semibold tracking-wide text-neutral-200 line-clamp-2">
                    {item.title}
                  </div>
                  {item.price && (
                    <div className="text-center text-[10px] font-bold tracking-wider text-amber-300 mt-1">
                      {item.price}
                    </div>
                  )}
                </div>
              );
              return item.link ? (
                <a
                  key={item.id || idx}
                  href={item.link}
                  className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 rounded-xl"
                >
                  {CardInner}
                </a>
              ) : (
                <div key={item.id || idx}>{CardInner}</div>
              );
            })}
          </div>
        )
      ) : (
        <div className="text-center text-sow-white/60 text-sm tracking-wide py-10">
          {section ? 'No items available right now.' : 'Loading menu...'}
        </div>
      )}
    </section>
  );
};

export default MenuSection;

/**
 * MenuSection component purpose:
 * - Reusable for any menu category (e.g., Southern Comforts, Beverages, Desserts)
 * - Provides two view modes (list w/ paginated cards, grid)
 * - Handles keyboard nav for list pagination
 * - Accepts configurable pageSize & initialViewMode
 */
